import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TaskRow } from '@/components/TaskRow';
import { Link } from 'react-router-dom';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import type { TaskState } from '@agentflow/shared';

const FILTERS: { label: string; value: 'all' | 'open' | TaskState }[] = [
  { label: 'All', value: 'all' },
  { label: 'Open', value: 'open' },
  { label: 'Triage', value: 'triage' },
  { label: 'Plan', value: 'plan' },
  { label: 'Execute', value: 'execute' },
  { label: 'Paused', value: 'paused' },
  { label: 'Succeeded', value: 'succeeded' },
  { label: 'Failed', value: 'failed' },
  { label: 'Cancelled', value: 'cancelled' },
];

const OPEN_STATES: TaskState[] = ['triage', 'plan', 'execute', 'paused'];

export function TasksPage(): JSX.Element {
  const [filter, setFilter] = useState<'all' | 'open' | TaskState>('open');

  const states =
    filter === 'all' ? undefined : filter === 'open' ? OPEN_STATES : ([filter] as TaskState[]);

  const query = useQuery({
    queryKey: ['tasks', filter],
    queryFn: () => api.listTasks({ state: states, limit: 100 }),
  });

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Tasks</h2>
          <p className="text-sm text-muted-foreground">
            All tasks across the system. Use filters to narrow.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button asChild>
            <Link to="/tasks/new">New task</Link>
          </Button>
        </div>
      </div>

      <Card>
        {query.isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {query.isError && (
          <div className="p-6 text-sm text-destructive">Failed to load tasks: {String(query.error)}</div>
        )}
        {query.data?.tasks.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No tasks match this filter.</div>
        )}
        {query.data?.tasks.map((task) => <TaskRow key={task.id} task={task} />)}
      </Card>
    </div>
  );
}
